import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import './styles/VerifySignUp.css';

function toMessage(value) {
  try {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (value instanceof Error) return value.message || String(value);
    if (Array.isArray(value)) {
      return value
        .map(item => toMessage(item))
        .filter(Boolean)
        .join('; ');
    }
    if (typeof value === 'object') {
      if (typeof value.message === 'string') return value.message;
      if (typeof value.detail === 'string') return value.detail;
      if (value.detail && typeof value.detail === 'object') {
        return toMessage(value.detail);
      }
      if (typeof value.error === 'string') return value.error;
      if (typeof value.error_description === 'string') return value.error_description;
    }
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function tryParseJson(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

function parseJsonDeep(value, maxDepth = 3) {
  let current = value;
  for (let i = 0; i < maxDepth; i += 1) {
    const parsed = tryParseJson(current);
    if (!parsed) break;
    current = parsed;
  }
  return current;
}

function getVerifyErrorMessage(payload, fallback = 'Verification failed.') {
  const parsedPayload = parseJsonDeep(payload);
  const parsedDetail = parseJsonDeep(parsedPayload?.detail);
  const wrapper = (parsedDetail && typeof parsedDetail === 'object') ? parsedDetail : parsedPayload;
  const nestedBody = parseJsonDeep(wrapper?.body);
  const nestedDetail = nestedBody?.detail;

  if (
    Number(wrapper?.status) === 409 &&
    /already\s+exists|already\s+confirmed/i.test(String(nestedDetail || ''))
  ) {
    return 'Your account was already confirmed. You can sign in.';
  }

  return (
    toMessage(nestedDetail) ||
    toMessage(wrapper?.detail) ||
    toMessage(parsedPayload?.detail) ||
    toMessage(parsedPayload) ||
    toMessage(payload) ||
    fallback
  );
}

export default function VerifySignUp() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams]);
  const [state, setState] = useState({ loading: true, kind: 'loading', message: '' });

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!token) {
        if (!cancelled) {
          setState({ loading: false, kind: 'error', message: 'Missing verification token.' });
        }
        return;
      }

      try {
        const resp = await fetch('/api/fastapi/auth/signup/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });

        if (!resp.ok) {
          const detail = await resp.json().catch(() => null);
          const message = getVerifyErrorMessage(detail);
          const alreadyVerified = /already\s+confirmed|already\s+verified/i.test(message);
          if (!cancelled) {
            setState({
              loading: false,
              kind: alreadyVerified ? 'already' : 'error',
              message,
            });
          }
          return;
        }

        const data = await resp.json().catch(() => null);
        const alreadyVerified = data?.status === 'already_verified';

        if (!cancelled) {
          setState({
            loading: false,
            kind: alreadyVerified ? 'already' : 'success',
            message: alreadyVerified
              ? 'Your account was already confirmed. You can sign in.'
              : 'Your account is now confirmed. You can sign in.',
          });
        }
      } catch (err) {
        if (!cancelled) {
          setState({ loading: false, kind: 'error', message: toMessage(err) || 'Verification failed.' });
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const isSuccess = state.kind === 'success' || state.kind === 'already';

  return (
    <div className="verify-page">
      <section className="verify-card" aria-live="polite">
        <p className="verify-eyebrow">PExA Small Airway Atlas</p>
        <h1 className="verify-title">Account verification</h1>
        <p className="verify-subtitle">Finish setting up your account to continue.</p>

        <div className={`verify-status verify-status-${state.kind}`}>
          {state.loading ? 'Verifying your account...' : state.message}
        </div>

        {!state.loading && (
          <div className="verify-actions">
            {isSuccess ? (
              <a className="verify-btn verify-btn-primary" href="/api/fastapi/auth/login">Go to sign in</a>
            ) : (
              <Link className="verify-btn verify-btn-secondary" to="/signup">Back to sign up</Link>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
